import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { applyBrandColor, brandPresets } from "@/lib/color";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Building2, Palette, Wallet, Languages, Sparkles, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { restaurant, refresh, user } = useAuth();

  const [company, setCompany] = useState({
    name: "", address: "", registration_number: "", vat_number: "",
    phone: "", contact_email: "", website: "", currency: "EUR", tax_rate: 20, timezone: "Europe/Vienna",
  });
  const [brand, setBrand] = useState({ brand_name: "", logo_url: "", favicon_url: "", primary_color: "#0F172A" });
  const [revenueMode, setRevenueMode] = useState("daily");
  const [saving, setSaving] = useState({ company: false, brand: false });

  useEffect(() => {
    if (!restaurant) return;
    setCompany({
      name: restaurant.name || "",
      address: restaurant.address || "",
      registration_number: restaurant.registration_number || "",
      vat_number: restaurant.vat_number || "",
      phone: restaurant.phone || "",
      contact_email: restaurant.contact_email || "",
      website: restaurant.website || "",
      currency: restaurant.currency || "EUR",
      tax_rate: restaurant.tax_rate ?? 20,
      timezone: restaurant.timezone || "Europe/Vienna",
    });
    setBrand({
      brand_name: restaurant.brand_name || restaurant.name || "",
      logo_url: restaurant.logo_url || "",
      favicon_url: restaurant.favicon_url || "",
      primary_color: restaurant.primary_color || "#0F172A",
    });
    setRevenueMode(restaurant.revenue_mode || "daily");
  }, [restaurant]);

  const saveCompany = async () => {
    setSaving((s) => ({ ...s, company: true }));
    try {
      await api.put("/restaurant", { name: company.name, address: company.address, tax_rate: parseFloat(company.tax_rate) || 20, timezone: company.timezone });
      await api.put("/restaurant/company-info", {
        registration_number: company.registration_number,
        vat_number: company.vat_number,
        phone: company.phone,
        contact_email: company.contact_email,
        website: company.website,
        currency: company.currency,
        tax_rate: parseFloat(company.tax_rate) || 20,
        address: company.address,
      });
      await refresh();
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving((s) => ({ ...s, company: false })); }
  };

  const saveBrand = async () => {
    setSaving((s) => ({ ...s, brand: true }));
    try {
      await api.put("/restaurant/brand", brand);
      applyBrandColor(brand.primary_color);
      await refresh();
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving((s) => ({ ...s, brand: false })); }
  };

  const saveRevenueMode = async (v) => {
    setRevenueMode(v);
    try { await api.put("/restaurant/revenue-mode", { revenue_mode: v }); await refresh(); toast.success(t("save")); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const saveLang = async (v) => { setLang(v); try { await api.put("/me/language", { language: v }); toast.success(t("save")); } catch { /* noop */ } };
  const previewColor = (hex) => { setBrand({ ...brand, primary_color: hex }); applyBrandColor(hex); };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <PageHeader title={t("settings")} description={user?.email} />

      <Tabs defaultValue="company">
        <TabsList className="flex flex-wrap h-auto p-1 bg-muted/50 rounded-xl">
          <TabsTrigger value="company" data-testid="tab-company" className="rounded-lg gap-1.5"><Building2 className="w-3.5 h-3.5" /> {t("companyInfo")}</TabsTrigger>
          <TabsTrigger value="brand" data-testid="tab-brand" className="rounded-lg gap-1.5"><Palette className="w-3.5 h-3.5" /> {t("brand")}</TabsTrigger>
          <TabsTrigger value="revenue-mode" data-testid="tab-revenue-mode" className="rounded-lg gap-1.5"><Wallet className="w-3.5 h-3.5" /> {t("revenueMode")}</TabsTrigger>
          <TabsTrigger value="language" data-testid="tab-language" className="rounded-lg gap-1.5"><Languages className="w-3.5 h-3.5" /> {t("language")}</TabsTrigger>
        </TabsList>

        {/* Company Info */}
        <TabsContent value="company" className="mt-6 space-y-4">
          <Card className="border rounded-xl shadow-elev-1">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base font-display font-semibold tracking-tight">
                {t("companyInfo")}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{t("companyInfoDesc")}</p>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <SectionTitle>{t("contactSection")}</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("restaurantName")}><Input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="c-name" className="h-10 rounded-lg" /></Field>
                <Field label={t("phone")}><Input value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} data-testid="c-phone" className="h-10 rounded-lg" /></Field>
                <Field label={t("contactEmail")}><Input type="email" value={company.contact_email} onChange={(e) => setCompany({ ...company, contact_email: e.target.value })} data-testid="c-email" className="h-10 rounded-lg" /></Field>
                <Field label={t("website")}><Input value={company.website} onChange={(e) => setCompany({ ...company, website: e.target.value })} placeholder="https://" data-testid="c-web" className="h-10 rounded-lg" /></Field>
              </div>

              <div className="border-t pt-5">
                <SectionTitle>{t("legalTaxSection")}</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <Field label={t("registrationNumber")}><Input value={company.registration_number} onChange={(e) => setCompany({ ...company, registration_number: e.target.value })} data-testid="c-reg" className="h-10 rounded-lg font-mono" /></Field>
                  <Field label={t("vatNumber")}><Input value={company.vat_number} onChange={(e) => setCompany({ ...company, vat_number: e.target.value })} data-testid="c-vat" className="h-10 rounded-lg font-mono" /></Field>
                  <Field label={t("currency")}>
                    <Select value={company.currency} onValueChange={(v) => setCompany({ ...company, currency: v })}>
                      <SelectTrigger data-testid="c-currency" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">€ EUR</SelectItem>
                        <SelectItem value="TRY">₺ TRY</SelectItem>
                        <SelectItem value="USD">$ USD</SelectItem>
                        <SelectItem value="GBP">£ GBP</SelectItem>
                        <SelectItem value="CHF">Fr CHF</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("taxRate")}>
                    <Input type="number" step="1" value={company.tax_rate} onChange={(e) => setCompany({ ...company, tax_rate: e.target.value })} data-testid="c-tax" className="h-10 rounded-lg font-mono" />
                  </Field>
                </div>
              </div>

              <div className="border-t pt-5">
                <SectionTitle>{t("address")}</SectionTitle>
                <Textarea rows={2} value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} data-testid="c-address" className="mt-3 rounded-lg" />
              </div>

              <div className="pt-3 flex justify-end">
                <Button onClick={saveCompany} disabled={saving.company} data-testid="save-company" className="h-10 rounded-lg shadow-elev-2">{saving.company ? t("loading") : t("save")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Brand */}
        <TabsContent value="brand" className="mt-6">
          <Card className="border rounded-xl shadow-elev-1">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base font-display font-semibold tracking-tight flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> {t("brand")}</CardTitle>
              <p className="text-xs text-muted-foreground">{lang === "tr" ? "Platform: RestroPilot · Restoran: senin markan" : "Plattform: RestroPilot · Restaurant: deine Marke"}</p>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("brandName")}><Input value={brand.brand_name} onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })} data-testid="brand-name" className="h-10 rounded-lg" /></Field>
                <Field label={t("logoUrl")}><Input value={brand.logo_url} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} data-testid="brand-logo" placeholder="https://…" className="h-10 rounded-lg" /></Field>
                <Field label={t("faviconUrl")}><Input value={brand.favicon_url} onChange={(e) => setBrand({ ...brand, favicon_url: e.target.value })} placeholder="https://…" className="h-10 rounded-lg" /></Field>
                <Field label={t("primaryColor")}>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brand.primary_color} onChange={(e) => previewColor(e.target.value)} className="w-11 h-10 rounded-lg border cursor-pointer p-0.5" data-testid="brand-color" />
                    <Input value={brand.primary_color} onChange={(e) => previewColor(e.target.value)} className="h-10 rounded-lg font-mono" />
                  </div>
                </Field>
              </div>
              <div>
                <SectionTitle>{t("presets")}</SectionTitle>
                <div className="flex flex-wrap gap-2.5 mt-3">
                  {brandPresets.map((p) => {
                    const active = brand.primary_color.toUpperCase() === p.hex.toUpperCase();
                    return (
                      <button
                        key={p.hex}
                        onClick={() => previewColor(p.hex)}
                        className={`w-9 h-9 rounded-full transition-all flex items-center justify-center ${active ? "ring-2 ring-offset-2 ring-foreground scale-110" : "ring-1 ring-black/5 hover:scale-105"}`}
                        style={{ background: p.hex }}
                        title={p.name}
                        data-testid={`preset-${p.name}`}
                      >
                        {active && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              </div>
              {brand.logo_url && (
                <div className="pt-4 flex items-center gap-3 border-t">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Preview</span>
                  <img src={brand.logo_url} alt="" className="w-11 h-11 rounded-lg border object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                  <span className="font-display font-bold tracking-tight">{brand.brand_name}</span>
                </div>
              )}
              <div className="pt-3 flex justify-end">
                <Button onClick={saveBrand} disabled={saving.brand} data-testid="save-brand" className="h-10 rounded-lg shadow-elev-2">{saving.brand ? t("loading") : t("save")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue mode */}
        <TabsContent value="revenue-mode" className="mt-6">
          <Card className="border rounded-xl shadow-elev-1">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base font-display font-semibold tracking-tight">{t("revenueMode")}</CardTitle>
              <p className="text-xs text-muted-foreground">{t("revenueModeSubtitle")}</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { v: "daily", label: t("revenueDaily"), desc: t("revenueDailyDesc") },
                  { v: "monthly", label: t("revenueMonthly"), desc: t("revenueMonthlyDesc") },
                  { v: "csv", label: t("revenueCsv"), desc: t("revenueCsvDesc") },
                ].map((m) => (
                  <button
                    key={m.v}
                    onClick={() => saveRevenueMode(m.v)}
                    className={`text-left border rounded-xl p-4 transition-all ${revenueMode === m.v ? "border-primary bg-primary/5 shadow-elev-2" : "hover:border-foreground/30 hover:shadow-elev-1"}`}
                    data-testid={`rmode-${m.v}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display font-semibold tracking-tight">{m.label}</div>
                      {revenueMode === m.v && <Badge className="text-[10px] rounded-md">{t("active")}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{m.desc}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language */}
        <TabsContent value="language" className="mt-6">
          <Card className="border rounded-xl shadow-elev-1">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base font-display font-semibold tracking-tight">{t("language")}</CardTitle>
              <p className="text-xs text-muted-foreground">{t("languageAutoNote")}</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { v: "en", flag: "EN", label: t("languageEn") },
                  { v: "de", flag: "DE", label: t("languageDe") },
                  { v: "tr", flag: "TR", label: t("languageTr") },
                ].map((l) => (
                  <button key={l.v} onClick={() => saveLang(l.v)} className={`text-left border rounded-xl p-4 transition-all flex items-center gap-3 ${lang === l.v ? "border-primary bg-primary/5 shadow-elev-2" : "hover:border-foreground/30 hover:shadow-elev-1"}`} data-testid={`lang-${l.v}`}>
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold text-sm ${lang === l.v ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      {l.flag}
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-semibold tracking-tight">{l.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{l.v.toUpperCase()}</div>
                    </div>
                    {lang === l.v && <Badge className="text-[10px] rounded-md">{t("active")}</Badge>}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{children}</div>;
}
